import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import RateGroupRuleService, { CreateRateGroupRuleBody, RateGroupRule } from '~/services/cruises/RateGroupRuleService';
import { RateGroup } from '~/services/cruises/RateGroupService';

import RateGroupingRatesModal from '../RateGroupingRatesModal';

import RateGroupRulesFilters from './RateGroupRulesFilters';
import RateGroupRulesItemByGroup from './RateGroupRulesItemByGroup';

interface Props {
  cruiseLine: CruiseLine;
  isFetchingList: boolean;
  rateGroupRules: Array<RateGroupRule>;
  rateGroups: Array<RateGroup>;
  setRateGroupRules: (rateGroupRules: Array<RateGroupRule>) => void;
}

function RateGroupRulesSection({ cruiseLine, rateGroupRules, rateGroups, setRateGroupRules }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedRateGroupId, setSelectedRateGroupId] = useState<string | null>(null);
  const [rateGroupRuleForm, setRateGroupRuleForm] = useState<CreateRateGroupRuleBody>({
    filters: [
      {
        filterType: 'RATE_CODE',
        rateCodePattern: '',
        matchType: 'PREFIX',
      },
    ],
  });

  const showConfirmDialog = useConfirmDialog();
  const { enqueueSnackbar } = useSnackbar();

  const isFormDirty = useMemo(() => {
    return editingId || selectedRateGroupId || rateGroupRuleForm.filters.length;
  }, [editingId, rateGroupRuleForm, selectedRateGroupId]);

  const isFormValid = useMemo(() => {
    return (
      selectedRateGroupId && rateGroupRuleForm.filters.every((filter) => filter.rateCodePattern && filter.matchType)
    );
  }, [rateGroupRuleForm, selectedRateGroupId]);

  const resetPackageForm = useCallback(() => {
    setEditingId(null);
    setRateGroupRuleForm({
      filters: [
        {
          filterType: 'RATE_CODE',
          rateCodePattern: '',
          matchType: 'PREFIX',
        },
      ],
    });
    setSelectedRateGroupId(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (isLoading || !isFormValid) {
      return;
    }

    setIsLoading(true);

    try {
      if (editingId) {
        await RateGroupRuleService.update(selectedRateGroupId, editingId, {
          filters: rateGroupRuleForm.filters,
        });

        const updatedList = rateGroupRules.map((rateGroupRule) =>
          rateGroupRule.id === editingId ? { ...rateGroupRule, ...rateGroupRuleForm } : rateGroupRule,
        );
        setRateGroupRules(updatedList as Array<RateGroupRule>);
      } else {
        const newPackageType = await RateGroupRuleService.create(selectedRateGroupId, rateGroupRuleForm);
        setRateGroupRules([newPackageType, ...rateGroupRules]);
      }

      enqueueSnackbar(editingId ? 'Rate group rule updated successfully' : 'Rate group rule created successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Error saving rate group rule', { variant: 'error' });
    } finally {
      setIsLoading(false);
      resetPackageForm();
    }
  }, [
    rateGroupRuleForm,
    resetPackageForm,
    editingId,
    isFormValid,
    selectedRateGroupId,
    rateGroupRules,
    enqueueSnackbar,
    setRateGroupRules,
    isLoading,
  ]);

  const handleEdit = useCallback(
    (rateGroupRule: RateGroupRule) => {
      setEditingId(rateGroupRule.id);

      setRateGroupRuleForm(rateGroupRule);
      setSelectedRateGroupId(rateGroupRule.rateGroupId);
    },
    [setEditingId, setRateGroupRuleForm, setSelectedRateGroupId],
  );

  const handleDelete = useCallback(
    async (rateGroupRule: RateGroupRule) => {
      if (isDeleting) {
        return;
      }

      const confirmed = await showConfirmDialog({
        title: 'Delete Rate Group Rule?',
        description: `This will delete the rate group rule, are you sure you want to proceed?`,
      });

      if (!confirmed) {
        return;
      }

      setIsDeleting(true);

      try {
        await RateGroupRuleService.deleteById(rateGroupRule.rateGroupId, rateGroupRule.id);
        setRateGroupRules(rateGroupRules.filter((rule) => rule.id !== rateGroupRule.id));

        enqueueSnackbar('Rate group rule deleted', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error deleting rate group rule', { variant: 'error' });
      } finally {
        setIsDeleting(false);
      }
    },
    [rateGroupRules, showConfirmDialog, enqueueSnackbar, setIsDeleting, setRateGroupRules, isDeleting],
  );

  return (
    <Stack direction="column" spacing={3}>
      <Stack direction="row" spacing={2}>
        <Typography variant="h6" gutterBottom>
          Rate Group Rules
        </Typography>

        <Button variant="text" onClick={() => setIsModalOpen(true)}>
          View cached rate codes
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="rate-group-type-label">Rate Group Type</InputLabel>
          <Select
            labelId="rate-group-type-label"
            value={selectedRateGroupId}
            disabled={!!editingId}
            label="Rate Group Type"
            onChange={(e) => setSelectedRateGroupId(e.target.value as string)}
          >
            {rateGroups.map((rateGroup) => (
              <MenuItem key={rateGroup.id} value={rateGroup.id}>
                {rateGroup.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <RateGroupRulesFilters rateGroupRuleForm={rateGroupRuleForm} setRateGroupRuleForm={setRateGroupRuleForm} />
        </FormControl>

        {isFormDirty && (
          <Button variant="outlined" onClick={resetPackageForm}>
            Cancel
          </Button>
        )}

        <Button variant="contained" onClick={handleSave}>
          {editingId ? 'Update' : 'Add'}
        </Button>
      </Stack>

      <Stack direction="column" spacing={2}>
        {rateGroups.map((rateGroup) => (
          <RateGroupRulesItemByGroup
            key={rateGroup.id}
            rateGroup={rateGroup}
            rateGroupRules={rateGroupRules}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        ))}

        {!rateGroups.length && (
          <Typography variant="body1" sx={{ color: 'text.secondary' }} align="center">
            No rate groups created
          </Typography>
        )}
      </Stack>

      <RateGroupingRatesModal cruiseLine={cruiseLine} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Stack>
  );
}

export default RateGroupRulesSection;
